import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Input,
  Modal,
  Spinner,
  useToast,
} from '@ohear/ui'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { AdminGuard } from '../../features/admin/AdminGuard'
import { AdminNav } from '../../features/admin/AdminNav'
import {
  type AdminFeatureFlag,
  useCreateFeatureFlag,
  useDeleteFeatureFlag,
  useFeatureFlags,
  useUpdateFeatureFlag,
} from '../../lib/api/admin'

export const Route = createFileRoute('/admin/feature-flags')({
  component: FeatureFlagsPage,
})

interface FlagDraft {
  default_enabled: boolean
  enabled_orgs: string
  enabled_percentage: number
}

function draftFromFlag(flag: AdminFeatureFlag): FlagDraft {
  return {
    default_enabled: flag.default_enabled,
    enabled_orgs: flag.enabled_orgs.join('\n'),
    enabled_percentage: flag.enabled_percentage,
  }
}

function FeatureFlagsPage() {
  const flags = useFeatureFlags()
  const create = useCreateFeatureFlag()
  const update = useUpdateFeatureFlag()
  const remove = useDeleteFeatureFlag()
  const toast = useToast()

  const [createOpen, setCreateOpen] = useState(false)
  const [createKey, setCreateKey] = useState('')
  const [createDefault, setCreateDefault] = useState(false)

  const [editing, setEditing] = useState<AdminFeatureFlag | null>(null)
  const [draft, setDraft] = useState<FlagDraft>({
    default_enabled: false,
    enabled_orgs: '',
    enabled_percentage: 0,
  })

  const items = flags.data?.items ?? []

  const startEdit = (flag: AdminFeatureFlag) => {
    setEditing(flag)
    setDraft(draftFromFlag(flag))
  }

  const submitCreate = () => {
    if (!createKey.trim()) {
      toast.show({ severity: 'warn', title: 'Key is required' })
      return
    }
    create.mutate(
      { key: createKey.trim(), default_enabled: createDefault },
      {
        onSuccess: () => {
          toast.show({ severity: 'success', title: `Created ${createKey.trim()}` })
          setCreateOpen(false)
          setCreateKey('')
          setCreateDefault(false)
        },
        onError: (err) =>
          toast.show({
            severity: 'error',
            title: 'Could not create flag',
            description: err instanceof Error ? err.message : 'Unknown error',
          }),
      },
    )
  }

  const submitEdit = () => {
    if (!editing) return
    const orgs = draft.enabled_orgs
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean)
    update.mutate(
      {
        key: editing.key,
        patch: {
          default_enabled: draft.default_enabled,
          enabled_orgs: orgs,
          enabled_percentage: draft.enabled_percentage,
        },
      },
      {
        onSuccess: () => {
          toast.show({ severity: 'success', title: `Updated ${editing.key}` })
          setEditing(null)
        },
        onError: (err) =>
          toast.show({
            severity: 'error',
            title: 'Could not save flag',
            description: err instanceof Error ? err.message : 'Unknown error',
          }),
      },
    )
  }

  const submitDelete = (flag: AdminFeatureFlag) => {
    if (!window.confirm(`Delete flag "${flag.key}"? This cannot be undone.`)) return
    remove.mutate(flag.key, {
      onSuccess: () => toast.show({ severity: 'success', title: `Deleted ${flag.key}` }),
      onError: (err) =>
        toast.show({
          severity: 'error',
          title: 'Could not delete flag',
          description: err instanceof Error ? err.message : 'Unknown error',
        }),
    })
  }

  return (
    <AdminGuard>
      <section className="flex flex-col gap-3" aria-labelledby="admin-flags-title">
        <header className="flex items-end justify-between gap-2">
          <div className="flex flex-col gap-1">
            <div className="text-[12px] uppercase tracking-wide text-[var(--text-3)]">
              Platform admin
            </div>
            <h1 id="admin-flags-title" className="text-[22px] font-semibold tracking-tight m-0">
              Feature flags
            </h1>
            <p className="text-[13px] text-[var(--text-2)] m-0">
              The default state controls every org. Allowlisted orgs override on; the percentage
              rolls out to the remaining tenants by hash bucket.
            </p>
          </div>
          <Button onClick={() => setCreateOpen(true)}>New flag</Button>
        </header>

        <AdminNav />

        <Card dense>
          {flags.isLoading ? (
            <div className="flex items-center gap-2 text-[var(--text-2)]">
              <Spinner /> Loading flags...
            </div>
          ) : items.length === 0 ? (
            <EmptyState title="No flags yet" description="Create one to start gating a feature." />
          ) : (
            <div className="overflow-x-auto -mx-3 -my-3">
              <table
                className="w-full text-[13px] border-separate"
                style={{ borderSpacing: 0 }}
                aria-label="Feature flags"
              >
                <thead
                  className="sticky top-0 z-10 text-left text-[11px] uppercase tracking-wide text-[var(--text-2)]"
                  style={{ background: 'var(--bg-3)' }}
                >
                  <tr>
                    <th className="px-3 py-2 font-medium">Key</th>
                    <th className="px-3 py-2 font-medium">Default</th>
                    <th className="px-3 py-2 font-medium">Allowlist</th>
                    <th className="px-3 py-2 font-medium text-right">Rollout</th>
                    <th className="px-3 py-2 font-medium" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((flag) => (
                    <tr key={flag.key} className="transition-colors hover:bg-[var(--bg-3)]">
                      <td
                        className="px-3 py-2 font-mono text-[12px] border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        {flag.key}
                      </td>
                      <td className="px-3 py-2 border-t" style={{ borderColor: 'var(--border)' }}>
                        {flag.default_enabled ? (
                          <Badge variant="green">on</Badge>
                        ) : (
                          <Badge variant="gray">off</Badge>
                        )}
                      </td>
                      <td
                        className="px-3 py-2 text-[var(--text-2)] tabular-nums border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        {flag.enabled_orgs.length} org{flag.enabled_orgs.length === 1 ? '' : 's'}
                      </td>
                      <td
                        className="px-3 py-2 text-right text-[var(--text-2)] tabular-nums border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        {flag.enabled_percentage}%
                      </td>
                      <td
                        className="px-3 py-2 text-right whitespace-nowrap border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        <Button size="sm" variant="ghost" onClick={() => startEdit(flag)}>
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => submitDelete(flag)}
                          loading={remove.isPending && remove.variables === flag.key}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Modal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          title="New feature flag"
          size="sm"
          footer={
            <>
              <Button
                variant="ghost"
                onClick={() => setCreateOpen(false)}
                disabled={create.isPending}
              >
                Cancel
              </Button>
              <Button onClick={submitCreate} loading={create.isPending}>
                Create
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-[13px]">
              <span className="font-medium">Key</span>
              <Input
                value={createKey}
                onChange={(e) => setCreateKey(e.target.value)}
                placeholder="e.g. enable_block_recording"
                autoFocus
              />
              <span className="text-[12px] text-[var(--text-2)]">
                Lowercase, dot/dash/underscore allowed.
              </span>
            </label>
            <Checkbox
              checked={createDefault}
              onChange={(e) => setCreateDefault(e.target.checked)}
              label="Enabled by default"
            />
          </div>
        </Modal>

        <Modal
          open={!!editing}
          onClose={() => setEditing(null)}
          title={editing ? `Edit ${editing.key}` : ''}
          size="md"
          footer={
            <>
              <Button variant="ghost" onClick={() => setEditing(null)} disabled={update.isPending}>
                Cancel
              </Button>
              <Button onClick={submitEdit} loading={update.isPending}>
                Save
              </Button>
            </>
          }
        >
          {editing ? (
            <div className="flex flex-col gap-3">
              <Checkbox
                checked={draft.default_enabled}
                onChange={(e) => setDraft((d) => ({ ...d, default_enabled: e.target.checked }))}
                label="Default enabled"
              />
              <label className="flex flex-col gap-1 text-[13px]">
                <span className="font-medium">Allowlisted org IDs</span>
                <textarea
                  value={draft.enabled_orgs}
                  onChange={(e) => setDraft((d) => ({ ...d, enabled_orgs: e.target.value }))}
                  rows={4}
                  className="w-full rounded border border-[var(--border)] p-2 text-[12px] font-mono"
                  placeholder="One UUID per line"
                />
                <span className="text-[12px] text-[var(--text-2)]">
                  These orgs see the flag enabled regardless of the default.
                </span>
              </label>
              <label className="flex flex-col gap-1 text-[13px]">
                <span className="font-medium">Rollout percentage</span>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={draft.enabled_percentage}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      enabled_percentage: Math.max(0, Math.min(100, Number(e.target.value) || 0)),
                    }))
                  }
                />
                <span className="text-[12px] text-[var(--text-2)]">
                  Applied to orgs not in the allowlist; bucketed by hash of org id.
                </span>
              </label>
            </div>
          ) : null}
        </Modal>
      </section>
    </AdminGuard>
  )
}
